import clsx from "clsx";
import Hammer from "hammerjs";
import { CiMap } from "react-icons/ci";
import { Tooltip } from "../common/tooltip";
import { useNavigate } from "react-router-dom";
import BedIcon from "../../assets/bedIcon.svg";
import BathIcon from "../../assets/bathIcon.svg";
import ArrowOpen from "../../assets/arrowOpen.svg";
import PinIcon from "../../assets/Cart/pinIcon.svg";
import { formatPrice } from "../../utils/formatPrice";
import { formatDate } from "../../utils/dateFormatter";
import { useMapStore } from "../../stores/mapCordinations";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import CirclePlusIcon from "../../assets/circlePlusIcon.svg";
import ListingXbutton from "../../assets/listingXButton.svg";
import { useCartComparison } from "./hooks/useCartComparison";
import React, { useCallback, useEffect, useRef } from "react";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { useMapFiltersStore } from "../../stores/filterMapStore";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { usePropertyStore } from "../../stores/newStores/cartComparisonStore";
import type { CartComparisonProperty } from "../../interfaces/comparisonProperty";
import {
  fieldTitles,
  sections,
} from "../../mocks/newMockData/comparisonProperties";

const CartComparisonTable = () => {
  const { scrollLeft, scrollRight, scrollRef, optionsRef, handleKeyDown } =
    useCartComparison();
  const { setSwitchMode } = useMapFiltersStore();
  const {
    properties,
    pinnedProperty,
    loading,
    expandedSections,
    showOptions,
    compareProperties,
    handlePin,
    toggleOptions,
    handleAddProperty,
    handleDeletePropertyToCompare,
    filteredPropertiesCompare,
    setPinnedProperty,
  } = usePropertyStore();

  useEffect(() => {
    setPinnedProperty(pinnedProperty ?? "0");
  }, [pinnedProperty, handlePin, properties]);

  const { setShowProperty, setHandleNavigate } = useMapStore();

  const navigate = useNavigate();

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        optionsRef.current &&
        !optionsRef.current.contains(event.target as Node)
      ) {
        toggleOptions();
      }
    },
    [toggleOptions]
  );

  useEffect(() => {
    if (showOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptions, handleClickOutside]);

  const hammerRef = useRef<HammerManager | null>(null);

  const scrollRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      const hammer = new Hammer(node);
      hammer.get("pan").set({ direction: Hammer.DIRECTION_HORIZONTAL });

      let isPanning = false;
      let velocity = 0;
      let lastDeltaX = 0;

      const snapToClosest = () => {
        const currentScroll = node.scrollLeft;
        const snapPoint = Math.round(currentScroll / 320) * 320;
        node.scrollTo({ left: snapPoint, behavior: "smooth" });
      };

      const applyMomentumScroll = () => {
        if (Math.abs(velocity) > 0.1) {
          node.scrollLeft -= velocity * 20;
          velocity *= 0.9;
          requestAnimationFrame(applyMomentumScroll);
        } else {
          snapToClosest();
        }
      };

      hammer.on("panstart", () => {
        isPanning = true;
        velocity = 0;
      });

      hammer.on("panmove", (event) => {
        node.scrollLeft -= event.deltaX - lastDeltaX;
        lastDeltaX = event.deltaX;
      });

      hammer.on("panend", (event) => {
        isPanning = false;
        lastDeltaX = 0;
        velocity = event.velocityX;

        if (Math.abs(velocity) < 0.1) {
          snapToClosest();
        } else {
          applyMomentumScroll();
        }
      });

      hammerRef.current = hammer;

      scrollRef.current = node;

      return () => {
        hammer.destroy();
        hammerRef.current = null;
      };
    }
  }, []);

  return (
    <>
      {!loading && properties.length > 0 && (
        <>
          <div className="flex items-center justify-between px-2 relative">
            <div className="rounded-full font-bold text-sm bg-z-abbey py-1 px-2 block lg:hidden">
              Checkout Comparison
            </div>
            <div className="flex items-center space-x-3 absolute right-0 -top-6">
              <button
                type="button"
                className="p-2 rounded-3xl border border-[#32302F]"
                onClick={scrollLeft}
              >
                <FiArrowLeft className="text-xl" />
              </button>
              <button
                type="button"
                className="p-2 rounded-3xl border border-[#32302F]"
                onClick={scrollRight}
              >
                <FiArrowRight className="text-xl" />
              </button>
            </div>
          </div>
          <div
            ref={scrollRefCallback}
            style={{ touchAction: "pan-x", willChange: "scroll-position" }}
            className="overflow-x-auto no-scrollbar cursor-grab"
          >
            <table className="border-collapse w-full">
              <thead className="w-full">
                <tr className="bg-white w-full">
                  <th className="sticky w-[250px] min-w-[250px] h-[240px] max-h-[240px] pt-8 pr-8 left-0 top-0 bg-white rounded-r-0 z-30 pb-10">
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                    <div
                      className="w-full h-full flex flex-col items-center justify-center border border-[rgba(50, 48, 47, 0.24)] rounded-lg cursor-pointer"
                      onClick={() => {
                        navigate("/");
                        setShowProperty(() => false);
                        setSwitchMode("map");
                      }}
                    >
                      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                      {/* <div
												className="flex flex-col w-full h-[calc(100%_-_1.875rem)] cursor-pointer justify-center items-center rounded-[.625rem]"
												onClick={toggleOptions}
											>
												<img src={AddHomeIcon} alt="" />
												<div className="text-sm mt-3 font-normal">
													Add more properties to compare
												</div>
											</div>
											<div className="text-z-dark-moss-green pointer-events-auto text-sm font-semibold absolute bottom-3 left-1/2 -translate-x-1/2">
												ALL DETAILS
											</div> */}
                      <CiMap className="text-[90px]" />
                      <div className="font-semibold">Go to Map</div>
                    </div>
                    {showOptions && (
                      <div
                        ref={optionsRef}
                        className="absolute top-full mt-2 shadow-lg drop-shadow-md rounded-[.625rem] text-sm w-[18.8125rem] bg-white text-start z-20"
                      >
                        {properties.map((property) => (
                          <button
                            type="button"
                            key={property.listingId}
                            onClick={() =>
                              handleAddProperty(property.listingId)
                            }
                            className="py-3 px-2 border-b w-full border-[#DCDCDC] cursor-pointer flex items-center justify-between"
                          >
                            <div>{property?.address?.address1}</div>

                            <div className="text-xl font-thin rounded-full flex items-center justify-center text-center">
                              <img src={CirclePlusIcon} alt="Add Property" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </th>
                  {properties.map((property) => (
                    <th
                      key={property.listingId}
                      className={`pr-3 min-w-[320px] max-w-[320px] h-[15.1875rem] min-h-[15.1875rem] pb-10 pt-7 ${
                        pinnedProperty === property.listingId
                          ? "sticky left-[250px] bg-white z-20 rounded-r-[.625rem]"
                          : ""
                      }`}
                    >
                      {pinnedProperty === property.listingId && (
                        <div className="text-z-shark font-medium absolute -top-[.3125rem]">
                          Pinned
                        </div>
                      )}
                      <div className="relative">
                        <div className="rounded-[.625rem] w-full pointer-events-none">
                          <img
                            src={property.displayImageUrl}
                            alt={property?.address?.address1}
                            className="w-full min-w-[8.75rem] h-full max-h-[15.1875rem] min-h-[15.1875rem] object-cover rounded-2xl"
                          />
                        </div>
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                        <div
                          onClick={() => {
                            navigate(`/listings/${property.listingId}`);
                            setShowProperty(() => false);
                            setHandleNavigate(() => true);
                          }}
                          className="absolute p-2.5 cursor-pointer right-0 bottom-0 bg-z-abbey rounded-br-[.625rem] rounded-tl-[.625rem]"
                        >
                          <img src={ArrowOpen} alt="Arrow Open Icon" />
                        </div>
                        <div
                          className="absolute cursor-pointer bottom-2 left-2 flex items-center justify-start rounded-full"
                          onClick={() => {
                            handlePin(property.listingId);
                          }}
                          onKeyDown={(e) =>
                            handleKeyDown(e, property.listingId)
                          }
                        >
                          <div
                            className={`w-[2rem] h-[2rem] drop-shadow-lg rounded-full flex items-center justify-center  ${
                              pinnedProperty === property.listingId
                                ? "bg-z-abbey"
                                : "bg-white"
                            }`}
                          >
                            <img width={16} src={PinIcon} alt="Pin Property" />
                          </div>
                        </div>
                        <div className="text-white font-bold text-xs absolute top-2 px-2 flex justify-between w-full items-center">
                          <div
                            className="truncate max-w-[calc(100%-1.625rem)]"
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {property?.address}
                          </div>
                          <button
                            type="button"
                            className="h-[1.375rem] drop-shadow-lg w-[1.375rem]"
                            onClick={() =>
                              handleDeletePropertyToCompare(property.listingId)
                            }
                          >
                            <img src={ListingXbutton} alt="Remove Property" />
                          </button>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sections.map((section, index) => (
                  <React.Fragment key={section.title}>
                    {/* <tbody
									// key={section.title}
									> */}
                    {/* <tr>
										<th
											className="p-4 text-left w-full flex items-center gap-2 sticky top-0 left-0 bg-white z-20 cursor-pointer"
											onClick={() => toggleSection(section.title)}
											onKeyDown={(e) => handleSectionKeyDown(e, section.title)}
										>
											{section.title}
											<img
												src={ArrowIcon}
												alt=""
												className={`transform transition-transform ${
													expandedSections[section.title] ? "rotate-90" : ""
												}`}
											/>
										</th>
									</tr> */}
                    {expandedSections[section.title] &&
                      section.fields.map((field, fieldIndex) => (
                        <tr
                          key={field}
                          className={
                            fieldIndex % 2 === 1 ? "bg-white" : "bg-[#EEFAFF]"
                          }
                        >
                          <td
                            className={`p-4 rounded-l-[0.7rem] font-bold text-base sticky left-0 z-20 ${
                              fieldIndex % 2 === 1 ? "bg-white" : "bg-[#EEFAFF]"
                            }`}
                          >
                            <span className="flex items-center">
                              {fieldTitles[field] || field}
                              {field === "listToSoldRatio" && (
                                <Tooltip title="The list-to-sold ratio shows the average percentage difference from the asking price that homes have sold for in this area over the past 60 days. For example, if a home listed at $500,000 sells for $515,000, the ratio is 3%.">
                                  <IoIosInformationCircleOutline className="ml-1 text-xl cursor-pointer" />
                                </Tooltip>
                              )}
                            </span>
                          </td>
                          {properties.map((property, i) => (
                            <td
                              key={property.listingId}
                              className={`text-base ${
                                fieldIndex % 2 === 1
                                  ? "bg-white"
                                  : "bg-[#EEFAFF]"
                              } ${
                                i === properties.length - 1
                                  ? "rounded-r-[1.75rem]"
                                  : ""
                              } ${
                                pinnedProperty === property.listingId
                                  ? "sticky left-[250px] z-20"
                                  : ""
                              }`}
                            >
                              <span
                                className={clsx(
                                  "flex items-center w-fit",

                                  {
                                    "bg-z-dark-moss-green px-2 py-1 rounded-md text-white":
                                      field === "downPaymentBoost",
                                    "text-z-dark-moss-green underline":
                                      field === "mlsNumber",
                                    "font-bold": field === "listPrice",
                                  }
                                )}
                              >
                                {field === "numBedrooms" && (
                                  <img className="mr-1" src={BedIcon} alt="" />
                                )}
                                {field === "numBathrooms" && (
                                  <img className="mr-1" src={BathIcon} alt="" />
                                )}
                                {field === "address.city"
                                  ? property.address.city
                                  : field === "downPaymentBoost"
                                  ? `$${formatPrice(property.downPaymentBoost)}`
                                  : field === "listToSoldRatio"
                                  ? property.listToSoldRatio?.toFixed(2)
                                  : [
                                      "listPrice",
                                      "tax",
                                      "pricePerSqft",
                                      "maintenanceFee",
                                    ].includes(field)
                                  ? property[
                                      field as keyof CartComparisonProperty
                                    ] != null
                                    ? `$${formatPrice(
                                        convertToFormattedAmount(
                                          property[
                                            field as keyof CartComparisonProperty
                                          ]
                                        )
                                      )}`
                                    : ""
                                  : field === "sqft"
                                  ? property.sqft
                                  : field === "lot"
                                  ? property.lot
                                  : field === "openHouseDate"
                                  ? Array.isArray(property.openHouseDate) &&
                                    property.openHouseDate.some(
                                      (date) => date !== null
                                    )
                                    ? (() => {
                                        const validDate =
                                          property.openHouseDate.find(
                                            (date) => date !== null
                                          );
                                        return validDate
                                          ? formatDate(validDate, "date")
                                          : "";
                                      })()
                                    : ""
                                  : property[
                                      field as keyof CartComparisonProperty
                                    ]}
                                {property.sqft && field === "sqft" && (
                                  <div className="ml-1">sqft</div>
                                )}
                                {field === "daysOnMarket" && (
                                  <div className="ml-1">days</div>
                                )}
                                {field === "listToSoldRatio" && <div>%</div>}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    {/* </tbody> */}
                    {index < sections.length - 1 && (
                      <tr className="h-[5rem] w-full before:content[''] before:w-full before:h-[.0625rem] relative before:absolute before:top-1/2">
                        <td colSpan={properties.length + 1} />
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export { CartComparisonTable };
